import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Calendar, dateFnsLocalizer, type View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import type { CalenderEventItem } from '../utils/CalenderEvents';
import { CalenderEvents } from '../utils/CalenderEvents';
import { Modal, Badge } from 'react-bootstrap';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalenderEventsCalendar.css';

const locales = { 'de': de };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

/** React-big-calendar event shape with our custom data. */
interface CalendarEvent {
    start: Date;
    end: Date;
    title: string;
    eventItem: CalenderEventItem;
}

function formatEventDate(dateStr: string): string {
    try {
        const d = new Date(dateStr);
        return Number.isNaN(d.getTime()) ? dateStr : format(d, 'dd.MM.yyyy', { locale: de });
    } catch {
        return dateStr;
    }
}

function getFirstNWords(text: string, n: number): string {
    if (!text || n <= 0) return '';
    const words = text.trim().split(/\s+/);
    return words.slice(0, n).join(' ');
}

function getMonthName(date: Date): string {
    return format(date, 'MMMM', { locale: de });
}

function buildEventTooltip(item: CalenderEventItem): string {
    const parts: string[] = [];
    parts.push(`Ende: ${formatEventDate(item.endDate)}`);
    if (item.city || item.country) {
        parts.push(`Ort: ${[item.city, item.country].filter(Boolean).join(', ')}`);
    }
    if (item.keywords) {
        const kw = item.keywords.split(',').map((k) => k.trim()).filter(Boolean).join(', ');
        if (kw) parts.push(`Keywords: ${kw}`);
    }
    return parts.join('\n');
}

/** Upcoming event card with calendar-page style layout and portal-based overlay. */
function UpcomingEventCard({ item, formatEventDate }: { item: CalenderEventItem; formatEventDate: (s: string) => string }) {
    const [showOverlay, setShowOverlay] = useState(false);
    const startDate = new Date(item.startDate);
    const dayNum = startDate.getDate();
    const monthName = getMonthName(startDate);
    const descPreview = getFirstNWords(item.description, 40);
    const keywords = item.keywords
        ? item.keywords.split(',').map((k) => k.trim()).filter(Boolean)
        : [];

    const overlay = showOverlay && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <div className="calender-event-card-overlay">
                <div
                    className="calender-event-card-overlay-backdrop"
                    onClick={() => setShowOverlay(false)}
                />
                <div className="calender-event-card-overlay-panel">
                    <div className="calender-event-card-overlay-header">
                        <h4 className="calender-event-card-overlay-title">{item.title}</h4>
                        <button
                            type="button"
                            className="calender-event-card-overlay-close"
                            onClick={() => setShowOverlay(false)}
                            aria-label="Schließen"
                        >
                            ×
                        </button>
                    </div>
                    <div className="calender-event-card-overlay-content">
                        <section className="calender-event-card-overlay-section">
                            <h6>Beschreibung</h6>
                            <p>{item.description}</p>
                        </section>
                        <section className="calender-event-card-overlay-section">
                            <h6>Details</h6>
                            <dl className="calender-event-card-overlay-details">
                                <div>
                                    <dt>Start</dt>
                                    <dd>{formatEventDate(item.startDate)}</dd>
                                </div>
                                <div>
                                    <dt>Ende</dt>
                                    <dd>{formatEventDate(item.endDate)}</dd>
                                </div>
                                {(item.city || item.country) && (
                                    <div>
                                        <dt>Ort</dt>
                                        <dd>{[item.city, item.country].filter(Boolean).join(', ')}</dd>
                                    </div>
                                )}
                                <div>
                                    <dt>Preis</dt>
                                    <dd>{item.price === 0 ? 'Kostenlos' : `${item.price} €`}</dd>
                                </div>
                                <div>
                                    <dt>Registrierung</dt>
                                    <dd>{item.needsRegister ? 'Ja' : 'Nein'}</dd>
                                </div>
                            </dl>
                        </section>
                        {keywords.length > 0 && (
                            <section className="calender-event-card-overlay-section">
                                <h6>Keywords</h6>
                                <div className="calender-event-card-overlay-keywords">
                                    {keywords.map((kw) => (
                                        <Badge key={kw} bg="light" className="text-dark">{kw}</Badge>
                                    ))}
                                </div>
                            </section>
                        )}
                        {(item.eventUrl1 || item.eventUrl2 || item.eventUrl3) && (
                            <section className="calender-event-card-overlay-section">
                                <h6>Links</h6>
                                <ul className="calender-event-card-overlay-links">
                                    {item.eventUrl1 && (
                                        <li><a href={item.eventUrl1} target="_blank" rel="noopener noreferrer">{item.eventUrl1}</a></li>
                                    )}
                                    {item.eventUrl2 && (
                                        <li><a href={item.eventUrl2} target="_blank" rel="noopener noreferrer">{item.eventUrl2}</a></li>
                                    )}
                                    {item.eventUrl3 && (
                                        <li><a href={item.eventUrl3} target="_blank" rel="noopener noreferrer">{item.eventUrl3}</a></li>
                                    )}
                                </ul>
                            </section>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        )
        : null;

    return (
        <>
            <div
                className="calender-event-card calender-event-card-clickable"
                onClick={() => setShowOverlay(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowOverlay(true);
                    }
                }}
            >
                <div className="calender-event-card-inner">
                    <div className="calender-event-card-month-bg">{monthName}</div>
                    <div className="calender-event-card-day">{dayNum}</div>
                    <h5 className="calender-event-card-title">{item.title}</h5>
                    {descPreview && (
                        <p className="calender-event-card-desc">{descPreview}…</p>
                    )}
                    <div className="calender-event-card-keywords">
                        {keywords.map((kw) => (
                            <Badge key={kw} bg="primary" className="me-1 mb-1">
                                {kw}
                            </Badge>
                        ))}
                    </div>
                </div>
            </div>
            {overlay}
        </>
    );
}

function EventComponent({ event }: { event: CalendarEvent }) {
    const tooltip = buildEventTooltip(event.eventItem);

    return (
        <div
            className="rbc-calender-event-content"
            title={tooltip}
        >
            <span className="rbc-calender-event-title">{event.title}</span>
        </div>
    );
}

export type OutputStyle = 'calendar' | 'list';

export interface CalenderEventsCalendarProps {
    date: Date;
    onDateChange?: (date: Date) => void;
    outputStyle?: OutputStyle;
}

function CalenderEventsCalendar({ date, onDateChange, outputStyle = 'calendar' }: CalenderEventsCalendarProps) {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [upcomingItems, setUpcomingItems] = useState<CalenderEventItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CalenderEventItem | null>(null);
    const [view, setView] = useState<View>('month');

    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    const loadEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const items = await CalenderEvents.getInstance().GetLatestEvents(year, month);
            const mapped: CalendarEvent[] = items.map((item) => {
                const start = new Date(item.startDate);
                const end = new Date(item.endDate);
                return {
                    start,
                    end,
                    title: item.title,
                    eventItem: item,
                };
            });
            setEvents(mapped);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load calendar events');
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    const loadUpcoming = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const items = await CalenderEvents.getInstance().GetUpcomingEvents(3);
            setUpcomingItems(items);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load upcoming events');
            setUpcomingItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (outputStyle === 'calendar') {
            loadEvents();
        } else {
            loadUpcoming();
        }
    }, [outputStyle, loadEvents, loadUpcoming]);

    const handleSelectEvent = useCallback((event: CalendarEvent) => {
        setSelectedEvent(event.eventItem);
    }, []);

    const handleCloseModal = useCallback(() => {
        setSelectedEvent(null);
    }, []);

    const handleNavigate = useCallback(
        (newDate: Date) => {
            onDateChange?.(newDate);
        },
        [onDateChange]
    );

    if (loading) {
        return (
            <div className="calender-events-calendar">
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading…</span>
                    </div>
                    <p className="mt-2 text-muted">Events werden geladen…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="calender-events-calendar">
                <div className="alert alert-danger">{error}</div>
            </div>
        );
    }

    const item = selectedEvent;

    return (
        <div className="calender-events-calendar">
            {outputStyle === 'list' ? (
                <div className="calender-events-upcoming-grid">
                    {upcomingItems.map((ev) => (
                        <UpcomingEventCard key={ev.id} item={ev} formatEventDate={formatEventDate} />
                    ))}
                    {upcomingItems.length === 0 && (
                        <p className="text-muted text-center py-5">Keine bevorstehenden Veranstaltungen.</p>
                    )}
                </div>
            ) : (
            <div className="calender-events-calendar-wrapper">
                <Calendar
                    localizer={localizer}
                    culture="de"
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    titleAccessor="title"
                    view={view}
                    onView={setView}
                    views={['month', 'week', 'day']}
                    date={date}
                    onNavigate={handleNavigate}
                    components={{
                        event: EventComponent,
                    }}
                    onSelectEvent={handleSelectEvent}
                    toolbar={true}
                    popup={true}
                    showAllEvents={false}
                />
            </div>
            )}

            <Modal show={item !== null} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{item?.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {item && (
                        <div className="calender-event-detail">
                            <p><strong>Description:</strong></p>
                            <p>{item.description}</p>

                            <p className="mb-1"><strong>Start:</strong> {formatEventDate(item.startDate)}</p>
                            <p className="mb-1"><strong>End:</strong> {formatEventDate(item.endDate)}</p>
                            <p className="mb-1"><strong>Country:</strong> {item.country}</p>
                            <p className="mb-1"><strong>City:</strong> {item.city}</p>
                            <p className="mb-1"><strong>Price:</strong> {item.price === 0 ? 'Kostenlos' : `${item.price} €`}</p>
                            <p className="mb-1"><strong>Registrierung erforderlich:</strong> {item.needsRegister ? 'Ja' : 'Nein'}</p>
                            {item.keywords && (
                                <p className="mb-1">
                                    <strong>Keywords:</strong>{' '}
                                    {item.keywords.split(',').map((k) => k.trim()).filter(Boolean).map((kw) => (
                                        <Badge key={kw} bg="primary" className="me-1">
                                            {kw}
                                        </Badge>
                                    ))}
                                </p>
                            )}
                            {(item.eventUrl1 || item.eventUrl2 || item.eventUrl3) && (
                                <p className="mb-0 mt-2"><strong>Links:</strong></p>
                            )}
                            {item.eventUrl1 && (
                                <p><a href={item.eventUrl1} target="_blank" rel="noopener noreferrer">{item.eventUrl1}</a></p>
                            )}
                            {item.eventUrl2 && (
                                <p><a href={item.eventUrl2} target="_blank" rel="noopener noreferrer">{item.eventUrl2}</a></p>
                            )}
                            {item.eventUrl3 && (
                                <p><a href={item.eventUrl3} target="_blank" rel="noopener noreferrer">{item.eventUrl3}</a></p>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default CalenderEventsCalendar;
